import type { ActionReducerMap } from '@ngrx/store';
import { createFeatureSelector, createSelector } from '@ngrx/store';

import type { ContentLanguageItem } from '../../core/models/common.models';
import type { ILocalizationSettings } from '../models/responses';
import { ContentLanguagesEnum } from '../../core/api/schema.enums';
import type { User } from '../models/user';
import * as fromUserInfo from './user-info/user-info.reducer';
import * as fromUserSettings from './user-settings/user-settings.reducer';

const isContentLanguage = (value: string): value is ContentLanguagesEnum => Object.values<string>(ContentLanguagesEnum).includes(value);

export interface UserState {
  userInfo: fromUserInfo.State;
  userSettings: fromUserSettings.State;
}

export const userReducers: ActionReducerMap<UserState> = {
  userInfo: fromUserInfo.reducer,
  userSettings: fromUserSettings.reducer,
};

const selectUserState = createFeatureSelector<UserState>('user');
const selectUserInfoState = createSelector(selectUserState, (state: UserState) => state.userInfo);
const selectUserSettingsState = createSelector(selectUserState, (state: UserState) => state.userSettings);
export const selectHasUserProjects = createSelector(selectUserInfoState, fromUserInfo.getHasProjects);
export const selectCurrentUser = createSelector(selectUserInfoState, fromUserInfo.getCurrentUser);
export const selectUserCounts = createSelector(selectUserInfoState, fromUserInfo.getUserCounts);
export const selectAdminUserCounts = createSelector(selectUserInfoState, fromUserInfo.getAdminUserCounts);
export const getUserSettings = createSelector(selectUserSettingsState, fromUserSettings.getLocalSettings);
export const getContentLanguages = createSelector(
  selectCurrentUser,
  getUserSettings,
  (user: User | undefined, settings: ILocalizationSettings | null): ContentLanguageItem[] => {
    if (user && settings) {
      return Object.entries(settings.content_languages).flatMap<ContentLanguageItem>(([k, v]) =>
        isContentLanguage(k) && user.company.content_languages.includes(k) ? [{ display: v[user.locale], value: k }] : [],
      );
    }
    return [];
  },
);
